import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import {
  ChatBotApiResponse,
  ChatMessagesResponseData,
  ChatSessionsResponseData,
  ChatSendPayload,
  ChatSendResponseData,
  ChatStreamChunk,
  ChatStreamApiResponse,
  SpeechToTextResponseData,
  ChatbotStatusResponseData,
} from '../pages/after-login-page/web/ai-counselor/ai-counselor.model';

@Injectable({ providedIn: 'root' })
export class AiCounselorService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly base = environment.base_url;

  /** Single source of truth for the currently active chat session, shared between
   *  the AI Counsellor page and the Copilot popup so either surface always reflects
   *  whichever conversation was most recently active in the other. */
  private readonly activeSessionIdSubject = new BehaviorSubject<string | null>(null);
  readonly activeSessionId$ = this.activeSessionIdSubject.asObservable();

  constructor() {
    // This root service outlives logout/login (no page reload), so drop the previous
    // user's active chat session whenever the logged-in user changes or logs out.
    this.authService.profileDetails$
      .pipe(
        map((profile) => profile?._id ?? null),
        distinctUntilChanged()
      )
      .subscribe(() => this.activeSessionIdSubject.next(null));
  }

  setActiveSessionId(chatSessionId: string | null): void {
    this.activeSessionIdSubject.next(chatSessionId);
  }

  /** Synchronous read of the current active session — lets a consumer check what's
   *  already known before deciding whether to seed it from e.g. a route query param. */
  get currentActiveSessionId(): string | null {
    return this.activeSessionIdSubject.value;
  }

  /**
   * POST /chatBot/stream — fetches the full SSE-formatted string from the JSON envelope,
   * then re-emits 3 characters per tick at 8ms to produce a live
   * typewriter streaming effect in the UI.
   *
   * Response envelope: { data: "data: {\"delta\":\"...\",\"chat_session_id\":\"...\"}\n\n..." }
   */
  streamMessage(payload: ChatSendPayload): Observable<ChatStreamChunk> {
    return new Observable<ChatStreamChunk>(observer => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let aborted = false;
      let onVisibilityChange: (() => void) | null = null;

      const cleanup = (): void => {
        aborted = true;
        if (timeoutId !== null) { clearTimeout(timeoutId); timeoutId = null; }
        if (onVisibilityChange) {
          document.removeEventListener('visibilitychange', onVisibilityChange);
          onVisibilityChange = null;
        }
      };

      const params = new HttpParams().set('baseurl', `${window.location.origin}/`);
      const httpSub = this.http
        .post<ChatStreamApiResponse>(`${this.base}chatBot/stream`, payload, { params })
        .subscribe({
          next: (res) => {
            // ── 1. Parse all `data:` lines from the SSE string, keeping track of ──
            //      each block's `event:` type (e.g. "badge") to route it separately
            //      from the plain message chunks used for the typewriter text.
            const chunks: ChatStreamChunk[] = [];
            let badgeKeys: string[] | undefined;
            let trophyKeys: string[] | undefined;
            for (const block of (res.data ?? '').split('\n\n')) {
              let eventType: string | null = null;
              for (const line of block.split('\n')) {
                if (line.startsWith('event:')) {
                  eventType = line.slice(6).trim();
                  continue;
                }
                if (!line.startsWith('data:')) continue;
                const raw = line.slice(5).trim();
                if (!raw) continue;
                try {
                  const parsed = JSON.parse(raw) as ChatStreamChunk;
                  if (eventType === 'badge') {
                    if (Array.isArray(parsed['badge_keys'])) badgeKeys = parsed['badge_keys'] as string[];
                    if (Array.isArray(parsed['newly_earned_trophies'])) trophyKeys = parsed['newly_earned_trophies'] as string[];
                  } else {
                    chunks.push(parsed);
                  }
                } catch { /* skip */ }
              }
            }

            // ── 2. Reassemble full text + grab session id ─────────────────
            const fullText  = chunks.map(c => c.delta ?? c.content ?? '').join('');
            const sessionId = chunks.find(c => c.chat_session_id)?.chat_session_id;

            if (sessionId) observer.next({ chat_session_id: sessionId });
            if (badgeKeys?.length || trophyKeys?.length) {
              observer.next({ badge_keys: badgeKeys, newly_earned_trophies: trophyKeys });
            }

            // ── 3. Emit 5 chars per tick at 10ms — 500 chars/sec ─────────
            const CHUNK_SIZE = 5;
            let i = 0;

            // Page Visibility: when tab regains focus, flush remaining text
            // instantly so the user never sees a frozen mid-stream message.
            onVisibilityChange = (): void => {
              if (document.hidden || aborted) return;
              if (timeoutId !== null) { clearTimeout(timeoutId); timeoutId = null; }
              if (i < fullText.length) {
                observer.next({ delta: fullText.slice(i) });
                i = fullText.length;
              }
              cleanup();
              observer.complete();
            };
            document.addEventListener('visibilitychange', onVisibilityChange);

            const tick = (): void => {
              if (aborted) return;
              if (i >= fullText.length) {
                cleanup();
                observer.complete();
                return;
              }
              const slice = fullText.slice(i, i + CHUNK_SIZE);
              observer.next({ delta: slice });
              i += slice.length;
              timeoutId = setTimeout(tick, 10);
            };
            tick();
          },
          error: err => { cleanup(); observer.error(err); },
        });

      return () => {
        cleanup();
        httpSub.unsubscribe();
      };
    });
  }

  /** POST /chatBot/chat — kept for fallback use */
  sendMessage(payload: ChatSendPayload): Observable<ChatBotApiResponse<ChatSendResponseData>> {
    const params = new HttpParams().set('baseurl', `${window.location.origin}/`);
    return this.http.post<ChatBotApiResponse<ChatSendResponseData>>(
      `${this.base}chatBot/chat`,
      payload,
      { params }
    );
  }

  /** GET /chatBot/chat-history/sessions — list sessions for the logged-in user.
   *  Same pagination shape as chat-history/messages:
   *  pass `before` cursor to load the next page; response returns `has_more` + `next_cursor`. */
  getSessions(options?: { before?: string; limit?: number }): Observable<ChatBotApiResponse<ChatSessionsResponseData>> {
    let params = new HttpParams().set('limit', String(options?.limit ?? 18));
    if (options?.before) params = params.set('before', options.before);
    return this.http.get<ChatBotApiResponse<ChatSessionsResponseData>>(
      `${this.base}chatBot/chat-history/sessions`,
      { params }
    );
  }

  /** GET /chatBot/chat-history/messages — messages for a session.
   *  Pass `before` (cursor) to load older pages; `limit` defaults to 15.
   *  Response includes `has_more` and `next_cursor` for pagination. */
  getMessages(
    chatSessionId: string,
    options?: { before?: string; limit?: number }
  ): Observable<ChatBotApiResponse<ChatMessagesResponseData>> {
    let params = new HttpParams()
      .set('chat_session_id', chatSessionId)
      .set('limit', String(options?.limit ?? 15))
      .set('baseurl', `${window.location.origin}/`);
    if (options?.before) {
      params = params.set('before', options.before);
    }
    return this.http.get<ChatBotApiResponse<ChatMessagesResponseData>>(
      `${this.base}chatBot/chat-history/messages`,
      { params }
    );
  }

  /** DELETE /chatBot/chat-history/sessions/:chat_session_id — delete a session */
  deleteSession(chatSessionId: string): Observable<ChatBotApiResponse<unknown>> {
    return this.http.delete<ChatBotApiResponse<unknown>>(
      `${this.base}chatBot/chat-history/sessions/${encodeURIComponent(chatSessionId)}`
    );
  }

  /** GET /user/chatbot?role=2 — this user's chatbot disable window, e.g. { disableStatus, from_date, to_date }. */
  getChatbotStatus(): Observable<ChatBotApiResponse<ChatbotStatusResponseData>> {
    const params = new HttpParams().set('role', '2');
    return this.http.get<ChatBotApiResponse<ChatbotStatusResponseData>>(`${this.base}user/chatbot`, { params });
  }

  /** POST /chatBot/speech-to-text — uploads a recorded voice clip; Groq API key stays server-side. */
  speechToText(audio: Blob, fileName = 'audio.webm'): Observable<ChatBotApiResponse<SpeechToTextResponseData>> {
    const formData = new FormData();
    formData.append('audio', audio, fileName);
    return this.http.post<ChatBotApiResponse<SpeechToTextResponseData>>(
      `${this.base}chatBot/speech-to-text`,
      formData
    );
  }
}
